import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyInventoryListing,
  CompanyAssetsListing,
  CompanyAssetCreation,
  CompanyAssetDetail,
  CompanyConsumableAssetCreation,
  CompanyAssetRequestDetails,
  CompanyWorkflowRequestDetail,
  CompanyRegularAssetsPage,
  CompanyConsumableAssetsPage,
  CompanyAssetRequestsPage,
  CompanyAssetSettingsPage,
} from "@/utils/lazyLoadComponents";
import { store } from "@/store";
import { companyAssetsApi } from "@/services/(company)/api.assets";
import { AssetRequestView } from "@/pages/(home)/(company)/assets/_components/AssetRequestView";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_ASSETS_DYNAMIC_ASSET_TYPE_ID_KEY = "assetTypeId";
export const COMPANY_ASSETS_DYNAMIC_ASSET_ID_KEY = "assetId";
export const COMPANY_ASSETS_DYNAMIC_CONSUMABLE_ID_KEY = "consumableAssetId";
export const COMPANY_ASSETS_DYNAMIC_REQUEST_ID_KEY = "requestId";
export const COMPANY_ASSETS_DYNAMIC_WORKFLOW_REQUEST_ID_KEY = "workflowRequestId";

export enum COMPANY_ASSETS_PATH_SEGMENTS {
  ROOT = "assets",
  REGULAR_ASSETS = "regular-assets",
  CONSUMABLE_ASSETS = "consumable-assets",
  ASSET_REQUESTS = "asset-requests",
  WORKFLOW_REQUESTS = "workflow-requests",
  CREATE = "create",
  ASSET_REQUEST = "asset-request",
  EDIT = "edit",
  ASSET_TYPE_DETAILS = `:${COMPANY_ASSETS_DYNAMIC_ASSET_TYPE_ID_KEY}`,
  ASSET_DETAILS = `:${COMPANY_ASSETS_DYNAMIC_ASSET_ID_KEY}`,
  CONSUMABLE_DETAILS = `:${COMPANY_ASSETS_DYNAMIC_CONSUMABLE_ID_KEY}`,
  REQUEST_DETAILS = `:${COMPANY_ASSETS_DYNAMIC_REQUEST_ID_KEY}`,
  WORKFLOW_REQUEST_DETAILS = `:${COMPANY_ASSETS_DYNAMIC_WORKFLOW_REQUEST_ID_KEY}`,
  // Module settings (moved out of Company Settings)
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyAssetsRoutes: RouteObject = {
  path: COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Assets" },
  HydrateFallback: Spinner,
  // No Component here — only the tab index pages get the page-level tabs layout
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyInventoryListing,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_ASSETS_PATH_SEGMENTS.REGULAR_ASSETS),
        },
        // ── Tab 1: Regular Assets ─────────────────────────────────────────
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.REGULAR_ASSETS,
          handle: { breadcrumb: "Regular Assets" },
          Component: CompanyRegularAssetsPage,
        },
        // ── Tab 2: Consumable Assets ──────────────────────────────────────
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.CONSUMABLE_ASSETS,
          handle: { breadcrumb: "Consumable Assets" },
          Component: CompanyConsumableAssetsPage,
        },
        // ── Tab 3: Asset Requests ─────────────────────────────────────────
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.ASSET_REQUESTS,
          handle: { breadcrumb: "Asset Requests" },
          Component: CompanyAssetRequestsPage,
        },
      ],
    },

    // ── Regular assets detail routes (no stats, no tabs) ────────────────────
    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks the dynamic siblings in React Router's
    // route ranking, so there is no collision. ──────────────────────────────
    {
      path: COMPANY_ASSETS_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Inventory & Assets Settings" },
      Component: CompanyAssetSettingsPage,
    },
    {
      path: COMPANY_ASSETS_PATH_SEGMENTS.REGULAR_ASSETS,
      handle: { breadcrumb: "Regular Assets" },
      children: [
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.ASSET_TYPE_DETAILS,
          loader: async ({ params }) => {
            const assetTypeId = Number(
              params[COMPANY_ASSETS_DYNAMIC_ASSET_TYPE_ID_KEY],
            );
            const { data: { data } = {} } = await store.dispatch(
              companyAssetsApi.endpoints.companyGetSingleAssetTypeList.initiate({
                assetTypeId,
                limit: 1,
              }),
            );
            const name = data?.[0]?.assetType.name ?? `Type ${assetTypeId}`;
            return { breadcrumbLabel: name };
          },
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel ?? "Asset Type",
          },
          children: [
            { index: true, Component: CompanyAssetsListing },
            {
              path: COMPANY_ASSETS_PATH_SEGMENTS.ASSET_DETAILS,
              loader: async ({ params }) => {
                const assetId = Number(
                  params[COMPANY_ASSETS_DYNAMIC_ASSET_ID_KEY],
                );
                const { data: { data } = {} } = await store.dispatch(
                  companyAssetsApi.endpoints.companyGetAssetDetailsById.initiate({
                    assetId,
                  }),
                );
                const name = data?.modelSoftware ?? `Asset ${assetId}`;
                return { breadcrumbLabel: name };
              },
              handle: {
                breadcrumb: (data: { breadcrumbLabel: string }) =>
                  data?.breadcrumbLabel ?? "Asset Details",
              },
              children: [
                { index: true, Component: CompanyAssetDetail },
                {
                  path: COMPANY_ASSETS_PATH_SEGMENTS.EDIT,
                  handle: { breadcrumb: "Edit" },
                  Component: CompanyAssetCreation,
                },
              ],
            },
          ],
        },
      ],
    },

    // ── Consumable assets detail routes (no stats, no tabs) ──────────────────
    {
      path: COMPANY_ASSETS_PATH_SEGMENTS.CONSUMABLE_ASSETS,
      handle: { breadcrumb: "Consumable Assets" },
      children: [
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.CREATE,
          handle: { breadcrumb: "Create Consumable" },
          Component: CompanyConsumableAssetCreation,
        },
        {
          path: `${COMPANY_ASSETS_PATH_SEGMENTS.EDIT}/${COMPANY_ASSETS_PATH_SEGMENTS.CONSUMABLE_DETAILS}`,
          loader: ({ params }) => ({
            breadcrumbLabel: `Consumable #${params[COMPANY_ASSETS_DYNAMIC_CONSUMABLE_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel,
          },
          Component: CompanyConsumableAssetCreation,
        },
      ],
    },

    // ── Create regular asset (no stats, no tabs) ─────────────────────────────
    {
      path: COMPANY_ASSETS_PATH_SEGMENTS.CREATE,
      handle: { breadcrumb: "Create" },
      Component: CompanyAssetCreation,
    },

    // ── Asset request detail routes (no stats, no tabs) ──────────────────────
    {
      path: COMPANY_ASSETS_PATH_SEGMENTS.ASSET_REQUESTS,
      handle: { breadcrumb: "Asset Requests" },
      children: [
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.REQUEST_DETAILS,
          handle: { breadcrumb: "Request Details" },
          Component: AssetRequestView,
        },
      ],
    },

    // ── Workflow request detail routes (no stats, no tabs) ───────────────────
    {
      path: COMPANY_ASSETS_PATH_SEGMENTS.WORKFLOW_REQUESTS,
      handle: { breadcrumb: "Workflow Requests" },
      children: [
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.WORKFLOW_REQUEST_DETAILS,
          loader: ({ params }) => ({
            breadcrumbLabel: `Workflow Request #${params[COMPANY_ASSETS_DYNAMIC_WORKFLOW_REQUEST_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel,
          },
          Component: CompanyWorkflowRequestDetail,
        },
      ],
    },

    // ── Asset request details — legacy / notification deep-link ─────────────
    {
      path: COMPANY_ASSETS_PATH_SEGMENTS.ASSET_REQUEST,
      handle: { breadcrumb: "Asset Requests" },
      children: [
        {
          path: COMPANY_ASSETS_PATH_SEGMENTS.REQUEST_DETAILS,
          loader: ({ params }) => ({
            breadcrumbLabel: `Request #${params[COMPANY_ASSETS_DYNAMIC_REQUEST_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel,
          },
          Component: CompanyAssetRequestDetails,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_ASSETS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_ASSETS_PATH_SEGMENTS.ROOT)),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.ASSETS.ROOT,
  SETTINGS_VTAB_ASSET_TYPES: MODULE_SETTINGS_PATHS.ASSETS.VTAB_ASSET_TYPES,
  SETTINGS_VTAB_ASSET_BRANDS: MODULE_SETTINGS_PATHS.ASSETS.VTAB_ASSET_BRANDS,

  REGULAR_ASSETS: withCompanyPrefix(
    addSlashes(
      COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
      COMPANY_ASSETS_PATH_SEGMENTS.REGULAR_ASSETS,
    ),
  ),
  CONSUMABLE_ASSETS: withCompanyPrefix(
    addSlashes(
      COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
      COMPANY_ASSETS_PATH_SEGMENTS.CONSUMABLE_ASSETS,
    ),
  ),
  ASSET_REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
      COMPANY_ASSETS_PATH_SEGMENTS.ASSET_REQUESTS,
    ),
  ),
  WORKFLOW_REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
      COMPANY_ASSETS_PATH_SEGMENTS.WORKFLOW_REQUESTS,
    ),
  ),
  CREATE: withCompanyPrefix(
    addSlashes(
      COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
      COMPANY_ASSETS_PATH_SEGMENTS.CREATE,
    ),
  ),
  CONSUMABLE_CREATE: withCompanyPrefix(
    addSlashes(
      COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
      COMPANY_ASSETS_PATH_SEGMENTS.CONSUMABLE_ASSETS,
      COMPANY_ASSETS_PATH_SEGMENTS.CREATE,
    ),
  ),
  CONSUMABLE_EDIT: (consumableAssetId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
        COMPANY_ASSETS_PATH_SEGMENTS.CONSUMABLE_ASSETS,
        COMPANY_ASSETS_PATH_SEGMENTS.EDIT,
        String(consumableAssetId),
      ),
    ),
  ASSET_TYPE: (assetTypeId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
        COMPANY_ASSETS_PATH_SEGMENTS.REGULAR_ASSETS,
        String(assetTypeId),
      ),
    ),
  ASSET_DETAIL: (assetTypeId: string | number, assetId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
        COMPANY_ASSETS_PATH_SEGMENTS.REGULAR_ASSETS,
        String(assetTypeId),
        String(assetId),
      ),
    ),
  ASSET_EDIT: (assetTypeId: string | number, assetId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
        COMPANY_ASSETS_PATH_SEGMENTS.REGULAR_ASSETS,
        String(assetTypeId),
        String(assetId),
        COMPANY_ASSETS_PATH_SEGMENTS.EDIT,
      ),
    ),
  ASSET_REQUEST_VIEW: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
        COMPANY_ASSETS_PATH_SEGMENTS.ASSET_REQUESTS,
        String(id),
      ),
    ),
  ASSET_REQUEST: (requestId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
        COMPANY_ASSETS_PATH_SEGMENTS.ASSET_REQUEST,
        String(requestId),
      ),
    ),
  WORKFLOW_REQUEST_DETAIL: (workflowRequestId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_ASSETS_PATH_SEGMENTS.ROOT,
        COMPANY_ASSETS_PATH_SEGMENTS.WORKFLOW_REQUESTS,
        String(workflowRequestId),
      ),
    ),
});
